import { useSearchParams } from "react-router";
import { useState } from 'react';
import { ContractSelect } from '@/components/form-fields/ContractSelectField/types/contractSelectFields';
import { Box, FormHelperText, Grid, Stack, TextField, Typography } from '@mui/material';
import ContractSelectField from '../../form-fields/ContractSelectField/components/ContractSelectField';
import { useTranslation } from 'react-i18next';
import MultiUploadButton from '../../MultiUploadButton';
import { Button } from "@gisce/oficina-virtual-components";
import { useSettingsContext } from '../../Settings/hooks/useSettingsContext';
import useCRMForm from '../hooks/useCRMForm';
import CategorySelectField from '../../form-fields/CRMCategorySelectField/components/CRMCategorySelectField';
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from "react-hook-form";
import { sendCRMFormData } from '../services/CRMForm';

const STATUS = {
  FORM: 0,
  SENDING: 1,
  SENT: 2,
};

const attachmentSchema = z.object({
  name: z.string(),
  content: z.string(),
});

const CRMForm = () => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();

  const { autolectures } = useCRMForm();
  const [searchParams] = useSearchParams();

  const [caseId, setCaseId] = useState<number | null>(null);
  const [status, setStatus] = useState(STATUS.FORM);

  const CRMFormSchema = z.object({
    contract: (z.object({
      id: z.number(),
      name: z.string(),
      type: z.enum(["electricity", "gas"]),
    }) satisfies z.ZodType<ContractSelect>)
    .nullable()
    .refine((val) => val !== null && val !== undefined, {
      message: t('common:text.required_field'),
    }),
    category: z.number({ invalid_type_error: t('common:text.required_field')}),
    subject: z.string().nonempty(t('common:text.required_field')),
    descripcio: z.string().nonempty(t('common:text.required_field')),
    attachments: z
      .array(attachmentSchema)
      .superRefine((attachments, ctx) => {
        attachments.forEach((file, index) => {
          if (
            !/^data:.*\/[a-zA-Z]+;base64,/.test(file.content)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:text.invalid_attachment_type'),
              path: [index, 'content'],
            });
          }
        });
      })
  })

  type CRMForm = z.infer<typeof CRMFormSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CRMForm>({
    resolver: zodResolver(CRMFormSchema),
    defaultValues: {
      contract: undefined,
      category: undefined,
      subject: "",
      descripcio: "",
      attachments: [],
    }
  });

  const onSubmit = async (data: CRMForm) => {
    setStatus(STATUS.SENDING);
    try {
      const postData = {
        polissa_id: data.contract.id,
        polissa_type: data.contract.type,
        categ_id: data.category,
        title: data.subject,
        message: data.descripcio,
        attachments: data.attachments,
      };

      const result = await sendCRMFormData(postData)
      setStatus(STATUS.SENT);
      setCaseId(result.id);
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus(STATUS.FORM);
    }
  }

  const renderDone = () => (
    <div>
      {t('common:text.contact_success')} <code>#{caseId}</code>.
    </div>
  );

  const renderForm = () => {
    const isSending = status === STATUS.SENDING;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2}>
          { autolectures ?
            <div>{t("common:text.contact_autolectures_sub")}</div>
          :
            (Settings.organization.phone_crm ? (
              <div>
                {t("common:text.contact_view_subtitle_with_phone")}
                <a href={`tel:${Settings.organization.phone_crm}`}>
                  {Settings.organization.phone_crm}
                </a>
              </div>
            ) : (
              <div>{t("common:text.contact_view_subtitle")}</div>
            ))
          }

          <Controller
            name="contract"
            control={control}
            render={({ field }) => (
              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                <ContractSelectField
                  value={field.value}
                  onChange={(val: ContractSelect) => field.onChange(val)}
                  hideIfEmpty={false}
                />
              </Box>
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <CategorySelectField
                onChange={field.onChange}
                autolectures={autolectures}
                unsigned={false}
                categoryCode={searchParams.get("category") ?? undefined}
                meta={{
                  error: errors.category?.message,
                  touched: !!errors.category,
                }}
              />
            )}
          />

          <Controller
            name="subject"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t('common:text.unsigned_contact_subject')+ "*"}
                  multiline={true}
                  minRows={5}
                  fullWidth={true}
                  error={!!errors.subject}
                />
                {errors?.subject?.message && (
                  <FormHelperText error={true} id="reclam-error-text">
                    {errors.subject.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="descripcio"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t('common:text.unsigned_contact_description')+ "*"}
                  multiline={true}
                  minRows={5}
                  fullWidth={true}
                  error={!!errors.descripcio}
                />
                {errors?.descripcio?.message && (
                  <FormHelperText error={true} id="reclam-error-text">
                    {errors.descripcio.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="attachments"
            control={control}
            render={({ field }) => (
              <MultiUploadButton
                value={field.value}
                label={t('common:text.attachment')}
                anotherLabel={t("common:text.attachment_add")}
                removeLabel={t("common:text.remove")}
                min={1}
                max={6}
                onChange={(files: File[]) => field.onChange(files)}
                accept={"image/png, image/jpeg, image/*,.pdf"}
              />
            )}
          />

          {Array.isArray(errors.attachments) &&
            errors.attachments.map((err, index) =>
              err?.content?.message ? (
                <FormHelperText key={index} error>
                  {t('common:text.attachment_filename')} {index + 1}: {err.content.message}
                </FormHelperText>
              ) : null
            )
          }

          <Button
            color={'primary'}
            variant={'contained'}
            loading={isSending}
            type="submit"
          >
            {t('common:text.contact_send')}
          </Button>
        </Stack>
      </form>
    );
  }

  const renderInfoText = () => {
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center">
        <Grid item>
          <Typography variant="body1" mb={2} fontWeight="bold">
            {t('common:text.crm_form_title')}{Settings.organization.name}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" mb={2}>
            {t('common:text.crm_form_info')}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" mb={2}>
            <span style={{ fontWeight: 'bold' }}>{t('common:text.crm_form_phone')}</span> {Settings.organization.phone_crm}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" mb={2}>
            <span style={{ fontWeight: 'bold' }}>{t('common:text.crm_form_email')}</span> {Settings.organization.email}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" mb={2} fontWeight="bold">
            {t('common:text.crm_form_extra_info')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      {Settings?.features?.crmFormDisabled ?
        renderInfoText()
      :
        <>
          <Box mb={2}>
            <Typography variant="h4" mb={2}>
              { autolectures ?
                t("common:text.contact_autolectures")
                :
                t("common:text.contact_contact")
              }
            </Typography>
          </Box>
          {(status === STATUS.FORM || status === STATUS.SENDING) && renderForm()}
          {status === STATUS.SENT && renderDone()}
        </>
      }
    </div>
  );
};

export default CRMForm;